/* eslint-disable max-lines */
import DCAIcon from '../../../../../assets/icons/GenAi/dca-icon.svg';
import NoData from '../../../../../assets/icons/GenAi/no-data.svg';
import NoRecord from '../../../../../assets/icons/GenAi/no-record.svg';
import FileUpload from '../../../../../assets/icons/GenAi/file-upload.svg';
import PDFFile from '../../../../../assets/icons/GenAi/pdf-file-icon.png';
import { STORAGE_CONSTANTS, storage } from '../../../../../libs/services/LocalStorage';
import {
  Button,
  Col,
  Card,
  Divider,
  Drawer,
  Row,
  Typography,
  message,
  Popover,
  Tabs,
  Modal,
  Upload,
} from 'antd';
import moment from 'moment';
import { isEmpty } from 'lodash';
import React, { useState, useEffect, useMemo } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { RcFile } from 'antd/lib/upload';
import { CloseCircleOutlined, CloudUploadOutlined, PlusCircleFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import CloseIcon from '../../../../../assets/icons/close-icon.svg';
import './DrawerOverHead.less';
import {
  ICreateSessionPayload,
  IArchiveChatPayload,
  IUploadGenAiDocumentPayload,
} from '../../../../../libs/models/GenAIModels';
import { GenAIActions } from '../../../../../libs/store/actions/GenAIActions';
import { IApplicationState, useAppSelector } from '../../../../../libs/store/reducers';
import Copy from '../../../../../assets/icons/GenAi/copy.svg';
import Regenerate from '../../../../../assets/icons/GenAi/regenerate.svg';
import FloatingIcon from '../../../../../assets/icons/GenAi/floating-icon.svg';
import Loader from '../../../../../components/ui/Loader/Loader';
import SearchBox from './SearchBox';
import GenAiTabs from './GenAiTabs';
import ChatSessionOptions from './ChatSessionOptions';
import SkillsAssistant from './SkillsAssistant/SkillsAssistant';

type CopilotMode = 'CopilotQueries' | 'DocumentAnalysis' | 'SkillsAssistant';

interface IDrawerOverHeadProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  query: string;
  cardContent?: string;
  setCardContent: React.Dispatch<React.SetStateAction<string | undefined>>;
  globalSearch: boolean;
  setIsGlobalSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

const DrawerOverHead: React.FC<IDrawerOverHeadProps> = ({
  visible,
  setVisible,
  setQuery,
  query,
  cardContent,
  setCardContent,
  globalSearch,
  setIsGlobalSearch,
}) => {
  const userId = storage.getItem(STORAGE_CONSTANTS.userId)!;
  const [selectedSearchHistoryId, setSelectedSearchHistoryId] = useState<string>();
  const [activeTab, setActiveTab] = useState(0);
  const [activePopover, setActivePopover] = useState<string>();
  const [isKeywordSearch, setIsKeywordSearch] = useState<boolean>(true);
  const [copilotMode, setCopilotMode] = useState<CopilotMode>('CopilotQueries');
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<RcFile>();
  const [isNewSession, setIsNewSession] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { isLoading, recentSearchList, currentChatSession } = useAppSelector(
    ({ genAI: { loading, loadingCount, recentSearchList, currentChatSession } }: IApplicationState) => ({
      isLoading: loading || loadingCount !== 0,
      recentSearchList,
      currentChatSession,
    }),
  );

  const handleSearchList = () => {
    setIsNewSession(true);
    setSelectedSearchHistoryId(undefined);
    setCardContent(undefined);
    setIsUploadOpen(!isKeywordSearch);
  };

  const dispatcher = {
    getRecentSearch: () => dispatch(GenAIActions.fetchRecentSearchListStart({ userId })),
    createOrUpdateGlobalSession: (payload: ICreateSessionPayload) =>
      dispatch(GenAIActions.currentChatSessionStart(payload)),
    archiveChat: (payload: IArchiveChatPayload) => dispatch(GenAIActions.archiveChatStart(payload)),
    uploadDocument: (payload: IUploadGenAiDocumentPayload) =>
      dispatch(GenAIActions.uploadDocumentStart(payload)),
  };

  const onCopySuccess = async () => {
    await message.success('Copied to clipboard.', 3);
  };
  useEffect(() => {
    if (!visible) return;
    if (globalSearch) {
      setIsKeywordSearch(true);
      setCopilotMode('CopilotQueries');
      setActiveTab(0);
      dispatcher.createOrUpdateGlobalSession({
        userId,
        query: cardContent || '',
        sessionId: userId + '_global',
        isDocSearch: false,
      });
    } else {
      dispatcher.getRecentSearch();
    }
    setIsNewSession(false);
    setSelectedSearchHistoryId(userId + '_global');
  }, [visible]);

  useEffect(() => {
    if (currentChatSession?.session_id && !selectedSearchHistoryId) {
      setIsNewSession(false);
      setSelectedSearchHistoryId(currentChatSession.session_id);
    }
  }, [currentChatSession]);

  const sortedListAndSelectedSearchItem = useMemo(() => {
    let selectedRecentSearchItem = undefined;
    const filteredRecentSearchList = recentSearchList.filter(({ isDocumentSearch }) =>
      isKeywordSearch ? !isDocumentSearch : isDocumentSearch,
    );
    if (!filteredRecentSearchList) {
      return {
        selectedRecentSearchItem,
        sortedList: [],
        nonArchivedLength: 0,
        archivedLength: 0,
      };
    }

    const nonArchivedList = filteredRecentSearchList.filter(
      (searchedItem) => !searchedItem.archived,
    );
    const archivedList = filteredRecentSearchList.filter((searchedItem) => searchedItem.archived);
    const selectedList = activeTab === 0 ? nonArchivedList : archivedList;

    const sortedList = [...selectedList].sort((searchItemA, searchItemB) => {
      if (searchItemA.sessionId === userId + '_global') {
        return -1;
      }
      if (searchItemB.sessionId === userId + '_global') {
        return 1;
      }
      return new Date(searchItemB.updatedAt).getTime() - new Date(searchItemA.updatedAt).getTime();
    });

    if (!isLoading && !isEmpty(selectedSearchHistoryId)) {
      selectedRecentSearchItem = selectedList.find(
        (recentSearch) => recentSearch.sessionId === selectedSearchHistoryId,
      );
    }

    return {
      selectedRecentSearchItem,
      sortedList,
      nonArchivedLength: nonArchivedList.length,
      archivedLength: archivedList.length,
    };
  }, [recentSearchList, selectedSearchHistoryId, activeTab, isKeywordSearch, isLoading, userId]);

  useEffect(() => {
    if (isLoading || isNewSession) return;
    if (isEmpty(sortedListAndSelectedSearchItem.selectedRecentSearchItem)) {
      const first = sortedListAndSelectedSearchItem.sortedList[0]?.sessionId;
      setSelectedSearchHistoryId(first ?? undefined);
    }
  }, [sortedListAndSelectedSearchItem, isLoading, isNewSession]);

  const formatDateTime = (inputDateString?: string) => {
    if (!inputDateString) {
      return { formattedDate: '', formattedTime: '' };
    }

    const formattedDate = moment.utc(inputDateString).local().format('MMM DD');
    const formattedTime = moment.utc(inputDateString).local().format('hh:mm A');

    return { formattedDate, formattedTime };
  };

  const formattedDateTime = formatDateTime(
    sortedListAndSelectedSearchItem.selectedRecentSearchItem?.updatedAt,
  );

  const tabs = [
    `Recent ${isKeywordSearch ? 'Activities' : 'Uploads'} ( ${
      sortedListAndSelectedSearchItem.nonArchivedLength
    } )`,
    `Archived${isKeywordSearch ? '' : ' Documents'} ( ${
      sortedListAndSelectedSearchItem?.archivedLength
    } )`,
  ];

  const renderDrawerHeader = () => {
    return (
      <>
        <span>{'DCA Copilot'}</span>
        <Tabs
          className="header-tabs"
          activeKey={copilotMode}
          onChange={(key) => {
            const mode = key as CopilotMode;
            setCopilotMode(mode);
            if (mode !== 'SkillsAssistant') {
              setIsKeywordSearch(mode === 'CopilotQueries');
            }
          }}
        >
          <Tabs.TabPane tab={'Copilot Queries'} key={'CopilotQueries'}></Tabs.TabPane>
          <Tabs.TabPane tab={'Document Analysis'} key={'DocumentAnalysis'}></Tabs.TabPane>
          <Tabs.TabPane tab={'Skills Assistant'} key={'SkillsAssistant'}></Tabs.TabPane>
        </Tabs>
      </>
    );
  };

  const handleUploadFile = () => {
    const blob = new Blob([uploadFile!], { type: uploadFile!.type });
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    })
      .then((text) => {
        dispatcher.uploadDocument({
          userId,
          fileName: uploadFile!.name,
          inputFile: text.replace('data:application/pdf;base64,', ''),
        });
      })
      .finally(() => {
        setIsUploadOpen(false);
        setUploadFile(undefined);
      });
  };

  return (
    <Drawer
      className="Gen-AI-drawer"
      title={renderDrawerHeader()}
      placement="right"
      width="100%"
      footer={null}
      closable={true}
      onClose={() => {
        setIsGlobalSearch(true);
        setVisible(false);
        setQuery('');
        setSelectedSearchHistoryId(undefined);
      }}
      open={visible}
      closeIcon={<img src={CloseIcon} alt="close" className="close-icon" />}
    >
      <Loader loading={isLoading} loaderName={''}>
        {copilotMode === 'SkillsAssistant' ? (
          <div className="drawer-container drawer-container--skills-assistant">
            <SkillsAssistant />
          </div>
        ) : (
        <Row className="drawer-container">
          <Col span={7}>
            <Card bordered={true} className="recent-searches-card">
              <GenAiTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
              <Divider />
              <div className="flex-column h-100 justify-space-between">
                {sortedListAndSelectedSearchItem.sortedList.length ? (
                  <div className="recent-searches-sidebar">
                    <div className="recent-search-body">
                      {sortedListAndSelectedSearchItem.sortedList.map((item) => (
                        <span
                          key={item.sessionId}
                          className={`chat-preview ${
                            item.sessionId === selectedSearchHistoryId ? 'selected' : ''
                          }`}
                          onClick={() => { setIsNewSession(false); setSelectedSearchHistoryId(item.sessionId); }}
                        >
                          <span className="session-name">
                            {isKeywordSearch ? (
                              item.queryList.slice(-1)[0]?.question
                            ) : (
                              <>
                                <img src={PDFFile} alt={'PDF'} />
                                {item.sessionName}
                              </>
                            )}
                          </span>
                          <Popover
                            placement="bottomLeft"
                            content={
                              <ChatSessionOptions
                                session={item}
                                archiveChat={dispatcher.archiveChat}
                                hidePopover={() => setActivePopover(undefined)}
                              />
                            }
                            trigger="click"
                            open={activePopover === item.sessionId}
                            onOpenChange={(viviblePopover) =>
                              setActivePopover(viviblePopover ? item.sessionId : undefined)
                            }
                            zIndex={100}
                            arrowPointAtCenter
                            className="popover"
                          >
                            <span className="hover-img">{'•••'}</span>
                          </Popover>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-record">
                    <img src={NoRecord} alt="no record" />
                    <Typography>{`No ${
                      isKeywordSearch ? 'records' : 'documents'
                    } available.`}</Typography>
                  </div>
                )}
                <Button className="new-session-button" onClick={handleSearchList}>
                  <Typography className="select-button-text">
                    {isKeywordSearch ? (
                      <>
                        <PlusCircleFilled /> {'Start New Session'}
                      </>
                    ) : (
                      <>
                        <img src={FileUpload} alt={'Upload File'} /> {'Upload Document'}
                      </>
                    )}
                  </Typography>
                </Button>
              </div>
              <Modal
                title="Upload Document"
                open={isUploadOpen}
                centered
                className="upload-file-modal"
                onCancel={() => setIsUploadOpen(false)}
                closeIcon={<CloseCircleOutlined />}
                footer={
                  uploadFile !== undefined ? (
                    <Button className="upload-file-button" onClick={handleUploadFile}>
                      {'Save'}
                    </Button>
                  ) : null
                }
              >
                <Upload.Dragger
                  name={'file'}
                  maxCount={1}
                  fileList={uploadFile ? [uploadFile] : []}
                  beforeUpload={async (file) => {
                    const isPDF = file.type === 'application/pdf';
                    if (!isPDF) {
                      await message.error(`${file.name} is not a pdf file`);
                    } else {
                      setUploadFile(file);
                    }
                    return false;
                  }}
                  progress={{
                    strokeColor: '#86BC26',
                    strokeWidth: 10,
                    format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
                  }}
                  iconRender={() => <img src={PDFFile} alt={'PDF'} />}
                  onRemove={() => setUploadFile(undefined)}
                  showUploadList={{
                    showRemoveIcon: true,
                    removeIcon: 'Remove',
                  }}
                >
                  <CloudUploadOutlined />
                  {'Drop File here, or'}&nbsp;
                  <Typography.Link>{'Browse'}</Typography.Link>
                </Upload.Dragger>
              </Modal>
            </Card>
          </Col>
          <Col className="drawer-right-body" span={17}>
            <div className="right-top">
              <div className="profile-icon-box">
                <img src={DCAIcon} alt="dca icon" />
              </div>
              <div className="search-box">
                <SearchBox
                  isDrawer={true}
                  isKeywordSearch={isKeywordSearch}
                  query={query}
                  setQuery={setQuery}
                  setShowModal={() => {}}
                  setVisible={setVisible}
                  setCardContent={setCardContent}
                  selectedSearchHistoryId={selectedSearchHistoryId}
                  setIsGlobalSearch={setIsGlobalSearch}
                />
              </div>
            </div>
            <Card bordered={true} className="search-results-card">
              {sortedListAndSelectedSearchItem.selectedRecentSearchItem ? (
                <div className="search-result-card-window">
                  <div className="date-time-container">
                    <div className="horizontal-rule" />
                    <Typography>
                      {`${formattedDateTime.formattedTime}, ${formattedDateTime.formattedDate}`}
                    </Typography>
                  </div>
                  <div className="question-answer-wrapper">
                    {sortedListAndSelectedSearchItem.selectedRecentSearchItem.queryList.map(
                      (questionSet, index) => (
                        <div key={index} className="question-answer-card">
                          <div className="question">
                            <img src={DCAIcon} alt="dca" />
                            <Typography className="question-text">
                              {questionSet.question}
                            </Typography>
                          </div>
                          <div className="chat-support">
                            <img src={FloatingIcon} className="floating-icon" alt="Floating chat" />
                            <div className="answer">
                              <pre>
                                <Typography>{questionSet.answer}</Typography>
                              </pre>
                              <div className="button-rectangle">
                                <CopyToClipboard text={questionSet.answer} onCopy={onCopySuccess}>
                                  <Button className="response-action-btns">
                                    <img src={Copy} alt="copy" />
                                    <Typography className="button-text">Copy</Typography>
                                  </Button>
                                </CopyToClipboard>
                                <Button
                                  className="response-action-btns ml-1"
                                  onClick={() => {
                                    dispatcher.createOrUpdateGlobalSession({
                                      userId,
                                      query: questionSet.question,
                                      sessionId: selectedSearchHistoryId,
                                      isDocSearch: !isKeywordSearch,
                                    });
                                  }}
                                >
                                  <img src={Regenerate} alt="regenerate" />
                                  <Typography className="button-text">Regenerate</Typography>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-data">
                  <img src={NoData} alt="no data" />
                  <b>
                    {isKeywordSearch
                      ? 'For any specific queries,'
                      : 'Please upload document first to continue'}
                  </b>
                  <span>
                    {isKeywordSearch
                      ? 'We recommend using the search feature...'
                      : 'using search feature... '}
                  </span>
                </div>
              )}
            </Card>
          </Col>
        </Row>
        )}
      </Loader>
    </Drawer>
  );
};

export default DrawerOverHead;
